/**
 * Legacy-semantics identity bridge (web-only, intentionally partial).
 *
 * A structurally-identical twin of the covered subset of SEMANTICS_SRC: same
 * slash-path keys and {light, dark} shape, but every value resolves to the
 * value the current theme renders for that role today (a frozen DS_COLOR.*
 * literal) rather than the new-brand SEMANTICS value. This lets the MUI palette
 * and color consumers adopt the semantics taxonomy structurally with no visual
 * change; the rebrand then becomes a single swap of this map for SEMANTICS, at
 * which point this file is deleted wholesale.
 *
 * Coverage is limited to the roles a current web consumer maps onto (the
 * theme-pinned palette roles plus the raw DS_COLOR.* read sites). Roles with no
 * current web consumer get no twin. Self-contained (its own resolver and nest
 * helper) so the real SEMANTICS layer stays untouched and this file deletes
 * cleanly later.
 *
 * Additive only: SEMANTICS_SRC, resolve(), the primitives and DS_* are untouched.
 */
import { DS_COLOR } from '../colors';
import { lit, semantic } from '../dtcg';
/**
 * The covered subset: each key is a SemanticPath (present in SEMANTICS_SRC),
 * each mode value a DS_COLOR.* literal sourced from the frozen export it
 * preserves. `satisfies Partial<Record<SemanticPath, …>>` is the compile-time
 * keyspace guard — it admits any subset of the SEMANTICS keyspace but rejects a
 * key not in it. `satisfies` (not an annotation) keeps the literal key types, so
 * `LegacyPath` stays the precise covered union.
 */
export const LEGACY_SEMANTICS_SRC = {
    // --- Surface ---
    'surface/default': semantic(lit(DS_COLOR.LIGHT_BACKGROUND_DEFAULT), lit(DS_COLOR.DARK_BACKGROUND_DEFAULT)),
    // Paper split (Plan 1): the rendered <Paper>/<Card> surface twins to the
    // translucent MuiPaper override value; the palette slot twins to the opaque
    // slot value. Each maps to the role whose new-brand value equals its own.
    'surface/card': semantic(lit(DS_COLOR.LIGHT_BACKGROUND_PAPER), lit(DS_COLOR.BLACK_20P)),
    'surface/card-solid': semantic(lit(DS_COLOR.LIGHT_BACKGROUND_PAPER), lit(DS_COLOR.DARK_BACKGROUND_PAPER)),
    'surface/input': semantic(lit(DS_COLOR.LIGHT_BACKGROUND_PAPER), lit(DS_COLOR.BLACK_20P)),
    // Value election: pinned at the theme's LinearProgress track (#0000001F), not
    // the stepper/progress-circle consumer sites' #00000042 — those are P1
    // outliers that converge at the rebrand flip.
    'surface/progress/track': semantic(lit(DS_COLOR.LIGHT_ACTION_FOCUS), lit(DS_COLOR.DARK_ACTION_FOCUS)),
    // Screening-graph data-viz scale: fixed hues that do not change with theme, so
    // light and dark are the same literal.
    'surface/graph/screening/minimal': semantic(lit(DS_COLOR.GRAPH_MINIMAL), lit(DS_COLOR.GRAPH_MINIMAL)),
    'surface/graph/screening/mild': semantic(lit(DS_COLOR.GRAPH_MILD), lit(DS_COLOR.GRAPH_MILD)),
    'surface/graph/screening/moderate': semantic(lit(DS_COLOR.GRAPH_MODERATE), lit(DS_COLOR.GRAPH_MODERATE)),
    'surface/graph/screening/moderately-severe': semantic(lit(DS_COLOR.GRAPH_MODERATE_SEVERE), lit(DS_COLOR.GRAPH_MODERATE_SEVERE)),
    'surface/graph/screening/severe': semantic(lit(DS_COLOR.GRAPH_SEVERE), lit(DS_COLOR.GRAPH_SEVERE)),
    'surface/feedback/error-subtle': semantic(lit(DS_COLOR.LIGHT_ERROR_LIGHT), lit(DS_COLOR.DARK_ERROR_LIGHT)),
    'surface/feedback/warning-subtle': semantic(lit(DS_COLOR.LIGHT_WARNING_LIGHT), lit(DS_COLOR.DARK_WARNING_LIGHT)),
    'surface/feedback/success-subtle': semantic(lit(DS_COLOR.LIGHT_SUCCESS_LIGHT), lit(DS_COLOR.DARK_SUCCESS_LIGHT)),
    'surface/feedback/info-subtle': semantic(lit(DS_COLOR.LIGHT_INFO_LIGHT), lit(DS_COLOR.DARK_INFO_LIGHT)),
    // Bold (filled) feedback surfaces. MUI paints a coloured Chip and the
    // light-mode filled Alert from palette[severity].main, so the palette's
    // `.main` slot is this role's reader. Pinned at today's *_MAIN; pairs with
    // text/on-feedback/*-bold below as the DS's documented fill+label pair.
    // These deliberately pin the same *_MAIN literals as the text/feedback/*
    // family below — a bold fill and a coloured label happen to share a value
    // today, but they are separate roles and diverge at the rebrand flip.
    'surface/feedback/error-bold': semantic(lit(DS_COLOR.LIGHT_ERROR_MAIN), lit(DS_COLOR.DARK_ERROR_MAIN)),
    'surface/feedback/warning-bold': semantic(lit(DS_COLOR.LIGHT_WARNING_MAIN), lit(DS_COLOR.DARK_WARNING_MAIN)),
    'surface/feedback/success-bold': semantic(lit(DS_COLOR.LIGHT_SUCCESS_MAIN), lit(DS_COLOR.DARK_SUCCESS_MAIN)),
    'surface/feedback/info-bold': semantic(lit(DS_COLOR.LIGHT_INFO_MAIN), lit(DS_COLOR.DARK_INFO_MAIN)),
    // Neutral hover overlay for controls on flat, fixed-colour surfaces (action
    // menus, popovers, dropdown rows) — the palette's action.hover, which MUI's
    // MenuItem/ListItemButton hover reads directly. Pinned at today's
    // *_ACTION_HOVER (4% light / 8% dark); both arms snap to the DS's symmetric
    // 5% rung at the flip.
    'surface/hover': semantic(lit(DS_COLOR.LIGHT_ACTION_HOVER), lit(DS_COLOR.DARK_ACTION_HOVER)),
    // Value election: pinned at today's *_SECONDARY_MAIN — the opaque purple the
    // chip fill renders now — not the new-brand translucent white/80. This keeps
    // the resting chip pixel-identical; the fill turns translucent at the rebrand
    // flip, which is the whole point of the re-role.
    'surface/nav-action': semantic(lit(DS_COLOR.LIGHT_SECONDARY_MAIN), lit(DS_COLOR.DARK_SECONDARY_MAIN)),
    // --- Text ---
    'text/heading': semantic(lit(DS_COLOR.LIGHT_TEXT_PRIMARY_HEADER), lit(DS_COLOR.DARK_TEXT_PRIMARY_HEADER)),
    'text/default': semantic(lit(DS_COLOR.LIGHT_TEXT_BODY), lit(DS_COLOR.DARK_TEXT_BODY)),
    'text/sub': semantic(lit(DS_COLOR.LIGHT_TEXT_SUB), lit(DS_COLOR.DARK_TEXT_SUB)),
    'text/disabled': semantic(lit(DS_COLOR.LIGHT_TEXT_DISABLED), lit(DS_COLOR.DARK_TEXT_DISABLED)),
    'text/on-interactive/default': semantic(lit(DS_COLOR.LIGHT_PRIMARY_TEXT_CONTRAST), lit(DS_COLOR.DARK_PRIMARY_TEXT_CONTRAST)),
    // Text and icons on a SUBTLE/SOFT interactive surface — the "-secondary"
    // suffix is a weight, not a brand. Reads legibly today only because this twin
    // is pinned at filled-surface contrast values; both modes drift vs the
    // new-brand value, where the paired surface is translucent.
    'text/on-interactive/secondary': semantic(lit(DS_COLOR.LIGHT_SECONDARY_TEXT_CONTRAST), lit(DS_COLOR.DARK_SECONDARY_TEXT_CONTRAST)),
    // Labels on the bold feedback surfaces (the DS's documented fill+label pair).
    // Pinned at today's *_TEXT_CONTRAST; the dark arms move at the flip, which is
    // also where today's white-on-#FFB4A8 error label stops failing contrast.
    'text/on-feedback/error-bold': semantic(lit(DS_COLOR.LIGHT_ERROR_TEXT_CONTRAST), lit(DS_COLOR.DARK_ERROR_TEXT_CONTRAST)),
    'text/on-feedback/warning-bold': semantic(lit(DS_COLOR.LIGHT_WARNING_TEXT_CONTRAST), lit(DS_COLOR.DARK_WARNING_TEXT_CONTRAST)),
    'text/on-feedback/success-bold': semantic(lit(DS_COLOR.LIGHT_SUCCESS_TEXT_CONTRAST), lit(DS_COLOR.DARK_SUCCESS_TEXT_CONTRAST)),
    'text/on-feedback/info-bold': semantic(lit(DS_COLOR.LIGHT_INFO_TEXT_CONTRAST), lit(DS_COLOR.DARK_INFO_TEXT_CONTRAST)),
    // Labels on the subtle feedback surfaces — the standard/outlined MuiAlert.
    // Raw hexes, not `lit(DS_COLOR.*)` like every other twin here, because MUI
    // computes this colour from `palette[severity].light` instead of reading a
    // constant — so today's value exists in no token file.
    'text/on-feedback/error-subtle': semantic(lit('#665E5D'), lit('#FFF7F6')),
    'text/on-feedback/warning-subtle': semantic(lit('#665E5B'), lit('#FFF7F4')),
    'text/on-feedback/success-subtle': semantic(lit('#536641'), lit('#ECFFDA')),
    'text/on-feedback/info-subtle': semantic(lit('#5B6166'), lit('#F4FAFF')),
    // Non-interactive accent text — the concierge / AI-journaling echo-back
    // heading treatment (the palette's text.secondary). Pinned at today's
    // *_TEXT_SECONDARY_HEADER; changes hue family at the flip (deep purple →
    // blue accent light, brand tint → neutral dark), which is a manifest line and
    // an open designer question, not drift.
    'text/accent': semantic(lit(DS_COLOR.LIGHT_TEXT_SECONDARY_HEADER), lit(DS_COLOR.DARK_TEXT_SECONDARY_HEADER)),
    'text/feedback/error': semantic(lit(DS_COLOR.LIGHT_ERROR_MAIN), lit(DS_COLOR.DARK_ERROR_MAIN)),
    'text/feedback/warning': semantic(lit(DS_COLOR.LIGHT_WARNING_MAIN), lit(DS_COLOR.DARK_WARNING_MAIN)),
    'text/feedback/success': semantic(lit(DS_COLOR.LIGHT_SUCCESS_MAIN), lit(DS_COLOR.DARK_SUCCESS_MAIN)),
    'text/feedback/info': semantic(lit(DS_COLOR.LIGHT_INFO_MAIN), lit(DS_COLOR.DARK_INFO_MAIN)),
    'text/interactive/primary-hover': semantic(lit(DS_COLOR.LIGHT_PRIMARY_MAIN), lit(DS_COLOR.DARK_PRIMARY_MAIN)),
    'text/interactive/secondary-hover': semantic(lit(DS_COLOR.LIGHT_SECONDARY_MAIN), lit(DS_COLOR.DARK_SECONDARY_MAIN)),
    // --- Interactive ---
    'interactive/primary': semantic(lit(DS_COLOR.LIGHT_PRIMARY_MAIN), lit(DS_COLOR.DARK_PRIMARY_MAIN)),
    'interactive/primary-pressed': semantic(lit(DS_COLOR.LIGHT_PRIMARY_DARK), lit(DS_COLOR.DARK_PRIMARY_DARK)),
    'interactive/primary-hover': semantic(lit(DS_COLOR.LIGHT_PRIMARY_DARK), lit(DS_COLOR.DARK_PRIMARY_DARK)),
    'interactive/secondary': semantic(lit(DS_COLOR.LIGHT_SECONDARY_MAIN), lit(DS_COLOR.DARK_SECONDARY_MAIN)),
    'interactive/secondary-pressed': semantic(lit(DS_COLOR.LIGHT_SECONDARY_DARK), lit(DS_COLOR.DARK_SECONDARY_DARK)),
    'interactive/secondary-hover': semantic(lit(DS_COLOR.LIGHT_SECONDARY_DARK), lit(DS_COLOR.DARK_SECONDARY_DARK)),
    'interactive/disabled': semantic(lit(DS_COLOR.LIGHT_ACTION_DISABLED_BACKGROUND), lit(DS_COLOR.DARK_ACTION_DISABLED_BACKGROUND)),
    // --- Icon ---
    // icon/disabled is deliberately not bridged: the Figma DS assigns disabled
    // control glyphs to text/disabled (whose twin is claimed by the palette's
    // text.disabled today-value), so the theme's disabled radio/checkbox reads
    // stay raw until the rebrand flip re-points them. A twin here would have no
    // consumer.
    // Pinned at the value every icon-coloured site renders today; the unchecked
    // radio/checkbox glyph is the outlier and converges at the rebrand flip.
    'icon/default': semantic(lit(DS_COLOR.LIGHT_ACTION_ACTIVE), lit(DS_COLOR.DARK_ACTION_ACTIVE)),
    'icon/feedback/error': semantic(lit(DS_COLOR.LIGHT_ERROR_MAIN), lit(DS_COLOR.DARK_ERROR_MAIN)),
    'icon/feedback/warning': semantic(lit(DS_COLOR.LIGHT_WARNING_MAIN), lit(DS_COLOR.DARK_WARNING_MAIN)),
    'icon/feedback/success': semantic(lit(DS_COLOR.LIGHT_SUCCESS_MAIN), lit(DS_COLOR.DARK_SUCCESS_MAIN)),
    'icon/feedback/info': semantic(lit(DS_COLOR.LIGHT_INFO_MAIN), lit(DS_COLOR.DARK_INFO_MAIN)),
    // --- Border ---
    'border/divider': semantic(lit(DS_COLOR.LIGHT_DIVIDER), lit(DS_COLOR.DARK_DIVIDER)),
    'border/input': semantic(lit(DS_COLOR.LIGHT_INPUT_LINE), lit(DS_COLOR.DARK_INPUT_LINE)),
    'border/disabled': semantic(lit(DS_COLOR.LIGHT_OUTLINE_BORDER), lit(DS_COLOR.DARK_OUTLINE_BORDER)),
    'border/error': semantic(lit(DS_COLOR.LIGHT_ERROR_MAIN), lit(DS_COLOR.DARK_ERROR_MAIN)),
    'border/interactive-focus': semantic(lit(DS_COLOR.LIGHT_FOCUS_OUTLINE), lit(DS_COLOR.DARK_FOCUS_OUTLINE)),
    // Value election: pinned at *_SECONDARY_MAIN, i.e. the same literal as the
    // chip fill. The chip's new base border is therefore invisible today — it
    // equals the surface it sits on, so no pixel moves — and becomes the design's
    // purple ring at the rebrand flip, where it is the only thing distinguishing a
    // translucent-white chip from the page behind it.
    'border/interactive-secondary': semantic(lit(DS_COLOR.LIGHT_SECONDARY_MAIN), lit(DS_COLOR.DARK_SECONDARY_MAIN)),
    // Value election: pinned at *_SECONDARY_DARK, the value the chip's hover used
    // to paint across its entire fill. The fill now shades with a neutral overlay
    // instead, so this literal carries the hover signal on the border.
    'border/interactive-secondary-hover': semantic(lit(DS_COLOR.LIGHT_SECONDARY_DARK), lit(DS_COLOR.DARK_SECONDARY_DARK)),
    // --- Gradient stops ---
    // Composite-gradient re-architecture: the 4 mappable gradient families are
    // rebuilt from these start/end stop twins by the theme's buildGradient helper
    // (which re-applies the frozen angle + shader overlay geometry DS_GRADIENT
    // bakes in). start ← *_TOP, end ← *_BOTTOM; foreground = the *_ICON_* stops.
    'gradient/background/primary start': semantic(lit(DS_COLOR.LIGHT_GRADIENT_BACKGROUND_PRIMARY_TOP), lit(DS_COLOR.DARK_GRADIENT_BACKGROUND_PRIMARY_TOP)),
    'gradient/background/primary end': semantic(lit(DS_COLOR.LIGHT_GRADIENT_BACKGROUND_PRIMARY_BOTTOM), lit(DS_COLOR.DARK_GRADIENT_BACKGROUND_PRIMARY_BOTTOM)),
    'gradient/background/secondary start': semantic(lit(DS_COLOR.LIGHT_GRADIENT_BACKGROUND_SECONDARY_TOP), lit(DS_COLOR.DARK_GRADIENT_BACKGROUND_SECONDARY_TOP)),
    'gradient/background/secondary end': semantic(lit(DS_COLOR.LIGHT_GRADIENT_BACKGROUND_SECONDARY_BOTTOM), lit(DS_COLOR.DARK_GRADIENT_BACKGROUND_SECONDARY_BOTTOM)),
    'gradient/foreground/primary start': semantic(lit(DS_COLOR.LIGHT_GRADIENT_ICON_PRIMARY_TOP), lit(DS_COLOR.DARK_GRADIENT_ICON_PRIMARY_TOP)),
    'gradient/foreground/primary end': semantic(lit(DS_COLOR.LIGHT_GRADIENT_ICON_PRIMARY_BOTTOM), lit(DS_COLOR.DARK_GRADIENT_ICON_PRIMARY_BOTTOM)),
    'gradient/foreground/secondary start': semantic(lit(DS_COLOR.LIGHT_GRADIENT_ICON_SECONDARY_TOP), lit(DS_COLOR.DARK_GRADIENT_ICON_SECONDARY_TOP)),
    'gradient/foreground/secondary end': semantic(lit(DS_COLOR.LIGHT_GRADIENT_ICON_SECONDARY_BOTTOM), lit(DS_COLOR.DARK_GRADIENT_ICON_SECONDARY_BOTTOM)),
};
/** PascalCase one slash-path segment, splitting on '-' and ' '. */
export function toPascal(seg) {
    return seg
        .split(/[-\s]/)
        .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : w))
        .join('');
}
/**
 * Nested PascalCase view of a flat slash-path map, mirroring TOKENS_SEMANTICS'
 * projection. Generic over the leaf type so this file's token map and the web
 * theme's mode-resolved *string* map (`theme.semantics`) share one projection
 * rather than each re-implementing it. (SEMANTICS_SRC keeps its own copy in
 * index.ts — that layer is deliberately untouched by the bridge.)
 */
export function nest(src) {
    const root = {};
    for (const [path, value] of Object.entries(src)) {
        const segs = path.split('/');
        let node = root;
        segs.forEach((seg, i) => {
            const key = toPascal(seg);
            if (i === segs.length - 1) {
                node[key] = value;
            }
            else {
                node[key] ??= {};
                node = node[key];
            }
        });
    }
    return root;
}
/** Nested PascalCase view, the parallel of TOKENS_SEMANTICS. */
export const TOKENS_LEGACY_SEMANTICS = nest(LEGACY_SEMANTICS_SRC);
/**
 * Resolve a covered legacy token to its concrete value for the given mode. A
 * self-contained copy of resolve() reading this map, so the real resolver stays
 * untouched. Every legacy value is a literal by construction.
 */
export function resolveLegacy(path, mode) {
    const token = LEGACY_SEMANTICS_SRC[path];
    if (!token)
        throw new Error(`Unknown legacy semantic token: ${path}`);
    return token[mode].$value;
}
//# sourceMappingURL=legacy.js.map